import { Analytics } from '@analytics';
import { Button, Chip, InputAdornment, Snackbar, TextField, Tooltip } from '@material-ui/core';
import { makeStyles } from '@material-ui/core/styles';
import { FilterNone as CopyIcon, InfoOutlined } from '@material-ui/icons';
import { Alert as MuiAlert } from '@material-ui/lab';
import QRCode from 'qrcode';
import React, { useEffect, useState } from 'react';
import CopyToClipboard from 'react-copy-to-clipboard';
import { useIntl } from 'react-intl';
import { useCSVReader } from 'react-papaparse';
import { useHistory } from 'react-router';
import DashboardShare from '../../../assets/images/DashboardShare.png';
import { StyleBreakpoints } from '../../../utils/constants';
import CustomizableModal from '../../Common/CustomizableModal';
import messages from './messages';

const useStyles = makeStyles(theme => ({
  input: {
    width: '100%',
    '& .MuiInputBase-root': {
      flexWrap: 'wrap'
    }
  },
  adornment: {
    flexWrap: 'wrap',
    gap: '5px',
    justifyContent: 'flex-start',
    alignItems: 'flex-start',
    flex: ' 0 1 auto',
    maxHeight: 'unset',
    height: 'auto',
    marginBottom: '14px'
  },
  companyName: {
    fontSize: '18px',
    padding: '0 0 2px 2px'
  },
  setNameButton: {
    order: -1,
    position: 'absolute',
    left: 0,

    [theme.breakpoints.down(StyleBreakpoints.sm)]: {
      marginRight: 'auto',
      position: 'static'
    }
  },
  inviteTooltip: {
    backgroundColor: '#1e293b',
    padding: '16px',
    borderRadius: '12px'
  }
}));

const InviteGuideModal = ({
  isOpen,
  setIsOpen,
  sendInvitesToGuides,
  companyName,
  isRecommendationEmail = false,
  inviteCode
}) => {
  const intl = useIntl();
  const classes = useStyles();
  const history = useHistory();
  const { CSVReader } = useCSVReader();
  const openInviteLink = `${window.location.origin}/signupfrominvite/${inviteCode}`;

  const [emails, setEmails] = useState([]);
  const [value, setValue] = useState('');
  const [error, setError] = useState(null);
  const [zoneHover, setZoneHover] = useState(false);
  const [showAlert, setShouldShowCopyAlert] = useState(false);

  function handleClose() {
    setIsOpen(false);
    setEmails([]);
    setValue('');
    setError(null);
  }

  useEffect(() => {
    if (isOpen) {
      Analytics.trackViewFeature('InviteGuidesByEmail');
      Analytics.trackViewFeature('CompanyInviteLink');
    }
  }, [isOpen]);

  const handleQRDownload = async () => {
    Analytics.trackUseFeature('CompanyInviteLink');
    const data = await QRCode.toDataURL(openInviteLink).catch(err => {
      console.error(err);
    });

    const a = document.createElement('a');
    a.download = `Tip-Direct-Open-Invite_${companyName}.png`;
    a.href = data;
    a.click();
  };

  const handleKeyDown = evt => {
    if (['Enter', 'Tab', 'Space'].includes(evt.key)) {
      evt.preventDefault();

      validateEmails();
    }
  };

  const validateEmails = () => {
    if (value.trim() && isValid(value.trim())) {
      setEmails([...emails, value.trim()]);
      setValue('');
    }
  };

  const handleChange = evt => {
    setValue(evt.target.value);
    setError(null);
  };

  const handleDelete = item => {
    setEmails(emails.filter(i => i !== item));
  };

  const handlePaste = evt => {
    evt.preventDefault();

    const paste = evt.clipboardData.getData('text');
    const newEmails = paste.match(/[\w\d.-]+@[\w\d.-]+\.[\w\d.-]+/g);

    if (newEmails) {
      const toBeAdded = newEmails.filter(newEmail => !isInList(newEmail));
      setEmails([...emails, ...toBeAdded]);
    }
  };

  const isValid = email => {
    let error = null;

    if (isInList(email)) {
      error = `${email} has already been added.`;
    }

    if (!isEmail(email)) {
      error = `${email} is not a valid email address.`;
    }

    if (error) {
      setError(error);
      return false;
    }

    return true;
  };

  const isInList = email => {
    return emails.includes(email);
  };

  const isEmail = email => {
    return /[\w\d.-]+@[\w\d.-]+\.[\w\d.-]+/.test(email);
  };

  const handleSetNameClick = () => {
    history.push(`/settings/account/`);
  };

  const isCompanyNameValid = companyName?.length > 2;

  const companyNameOrDefaultString = isCompanyNameValid
    ? companyName
    : intl.formatMessage(messages.nameNotSet);

  const addEmails = data => {
    let emailIndex;
    let isolatedEmails = [];

    for (let i = 0; i < 2; i++) {
      for (let j = 0; j < data[i].length; j++) {
        if (isEmail(data[i][j])) {
          emailIndex = j;
          break;
        }
      }
    }

    if (emailIndex > -1) {
      data.map(row => {
        isEmail(row[emailIndex]) && isolatedEmails.push(row[emailIndex]);
      });
      error && setError(null);
    } else {
      setError('Valid email address not present in first two lines of file');
    }

    isolatedEmails.length > 0 && setEmails([...emails, ...isolatedEmails]);
  };

  const getModalBody = () => {
    return (
      <div className="w-[80vw] max-w-[600px]">
        <div className="flex justify-between items-center">
          <div>
            <h3 className="text-lg font-bold text-slate-800 tracking-tight mt-5 mb-0">
              {isRecommendationEmail ? (
                intl.formatMessage(messages.recommendTitle)
              ) : (
                <>
                  {intl.formatMessage(messages.inviteGuide)}:{' '}
                  <Button
                    color="primary"
                    className={`${classes.companyName} !text-indigo-600 !normal-case !font-semibold`}
                    onClick={isCompanyNameValid ? null : handleSetNameClick}>
                    {companyNameOrDefaultString}
                  </Button>
                </>
              )}
            </h3>
            <p className="text-slate-500 mt-1 mb-8">
              {intl.formatMessage(
                isRecommendationEmail ? messages.recommendEnterAnEmail : messages.enterAnEmail
              )}
            </p>
          </div>
        </div>
        <TextField
          multiline
          className={classes.input}
          label={intl.formatMessage(messages.emailAddress)}
          placeholder={intl.formatMessage(messages.placeholder)}
          variant="outlined"
          value={value}
          onKeyDown={handleKeyDown}
          onChange={handleChange}
          onBlur={validateEmails}
          onPaste={handlePaste}
          InputProps={{
            startAdornment: (
              <InputAdornment className={classes.adornment}>
                {emails.map(item => (
                  <Chip key={item} label={item} onDelete={() => handleDelete(item)} />
                ))}
              </InputAdornment>
            )
          }}
        />
        <CSVReader
          onUploadAccepted={results => {
            addEmails(results.data);
            setZoneHover(false);
          }}
          onDragOver={event => {
            event.preventDefault();
            setZoneHover(true);
          }}
          onDragLeave={event => {
            event.preventDefault();
            setZoneHover(false);
          }}>
          {({ getRootProps }) => (
            <>
              <div
                {...getRootProps()}
                className={`w-full border-2 border-dashed cursor-pointer relative box-border rounded-2xl flex flex-col items-center justify-center p-5 mt-5 text-slate-500 transition-colors ${
                  zoneHover ? 'border-indigo-400 bg-indigo-50/50' : 'border-slate-300 bg-slate-100'
                }`}>
                {intl.formatMessage(messages.dragAndDrop)}
              </div>
            </>
          )}
        </CSVReader>
        {error ? <p className="text-red-600 text-sm font-medium">{error}</p> : <p />}
        <div className="flex justify-end">
          <Button
            variant="contained"
            color="primary"
            className="!bg-indigo-600 hover:!bg-indigo-700 !text-white !font-semibold !px-5 !py-2.5 !rounded-xl !shadow-sm !normal-case disabled:!bg-slate-300 disabled:!text-white"
            disabled={!emails.length}
            onClick={() => {
              sendInvitesToGuides({ emails, isResend: false, isRecommendationEmail });
              Analytics.trackUseFeature('InviteGuides');
              Analytics.track(
                isRecommendationEmail ? 'recommend to a friend clicked' : 'invite guides clicked',
                { number_of_emails: emails.length }
              );
              handleClose();
            }}>
            {intl.formatMessage(messages.sendInvite)}
          </Button>
        </div>

        {!isRecommendationEmail && (
          <div>
            <Tooltip
              className="w-fit"
              title={
                <div>
                  <img
                    src={DashboardShare}
                    alt="Preview"
                    width={'100%'}
                    style={{ borderRadius: '8px' }}
                  />
                  <p>{intl.formatMessage(messages.tooltipTitle)}</p>
                </div>
              }
              classes={{
                tooltip: classes.inviteTooltip
              }}
              placement="top">
              <h3 className="text-base font-semibold text-slate-600 flex items-center gap-1">
                {intl.formatMessage(messages.companyInviteLink)}
                <InfoOutlined className="text-slate-400" style={{ width: 20 }} />
              </h3>
            </Tooltip>
            <div className="flex flex-row gap-4 mb-5">
              <CopyToClipboard text={openInviteLink} onCopy={() => setShouldShowCopyAlert(true)}>
                <Button
                  color="primary"
                  variant="contained"
                  fullWidth
                  className="!bg-indigo-600 hover:!bg-indigo-700 !text-white !font-semibold !px-5 !py-2.5 !rounded-xl !shadow-sm !normal-case">
                  <div>{intl.formatMessage(messages.copyLink)}</div>
                  <CopyIcon
                    className="self-center ml-1 cursor-pointer float-right"
                    style={{ width: 17, height: 17 }}
                  />
                </Button>
              </CopyToClipboard>
              <Button
                color="primary"
                variant="contained"
                fullWidth
                onClick={handleQRDownload}
                className="!bg-indigo-600 hover:!bg-indigo-700 !text-white !font-semibold !px-5 !py-2.5 !rounded-xl !shadow-sm !normal-case">
                <div>{intl.formatMessage(messages.downloadQRCode)}</div>
              </Button>
            </div>
          </div>
        )}
      </div>
    );
  };

  const getCustomButtons = () => {
    return isCompanyNameValid
      ? []
      : [
          <Button
            key="1"
            className={`${classes.setNameButton} !bg-indigo-600 hover:!bg-indigo-700 !text-white !font-semibold !px-5 !py-2.5 !rounded-xl !shadow-sm !normal-case`}
            onClick={handleSetNameClick}
            color="primary">
            {intl.formatMessage(messages.setName)}
          </Button>
        ];
  };

  return (
    <>
      <CustomizableModal
        modern
        isOpen={isOpen}
        onClose={handleClose}
        withHeader={false}
        withFooter={false}
        body={getModalBody()}
        customButtons={getCustomButtons()}
      />
      <Snackbar
        open={showAlert}
        onClose={() => {
          setShouldShowCopyAlert(false);
        }}
        anchorOrigin={{ vertical: 'top', horizontal: 'center' }}>
        <MuiAlert
          variant="filled"
          elevation={6}
          severity="info"
          onClose={() => {
            setShouldShowCopyAlert(false);
          }}>
          {intl.formatMessage(messages.inviteLinkCopied)}
        </MuiAlert>
      </Snackbar>
    </>
  );
};

export default InviteGuideModal;
